import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { firebaseApp } from "../../firebase";
import { getFirestore, doc, getDoc } from "firebase/firestore";
import {
  Briefcase,
  MapPin,
  Calendar,
  DollarSign,
  Clock,
  Building,
  ArrowLeft,
  GraduationCap,
  Globe
} from "lucide-react";

// Helper components
const DetailItem = ({ icon: Icon, label, value }) => (
  <div className="flex items-start gap-3">
    <div className="p-2 bg-red-50 rounded-lg text-red-600">
      <Icon className="w-5 h-5" />
    </div>
    <div>
      <p className="text-xs text-gray-500 font-medium uppercase tracking-wide">{label}</p>
      <p className="text-gray-900 font-semibold">{value || "Not specified"}</p>
    </div>
  </div>
);

const JobDetails = () => {
  const { jobId } = useParams();
  const [job, setJob] = useState(null);
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!jobId) return;

    const fetchData = async () => {
      try {
        const db = getFirestore(firebaseApp);

        // 1. Fetch Job
        const jobRef = doc(db, "hrJobs", jobId);
        const jobSnap = await getDoc(jobRef);

        if (jobSnap.exists()) {
          const jobData = { id: jobSnap.id, ...jobSnap.data() };
          setJob(jobData);

          // 2. Fetch HR/Company Profile
          if (jobData.hrId) {
            const hrRef = doc(db, "hrProfiles", jobData.hrId);
            const hrSnap = await getDoc(hrRef);
            if (hrSnap.exists()) {
              setCompany(hrSnap.data());
            }
          }
        }
      } catch (err) {
        console.error("Failed to fetch details:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [jobId]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600"></div>
      </div>
    );
  }

  if (!job) {
    return (
      <div className="text-center py-20">
        <h2 className="text-xl font-bold text-gray-900">Job not found</h2>
        <Link to="/hr/my-jobs" className="text-red-600 hover:text-red-700 font-medium mt-2 inline-block">
          Return to My Jobs
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto pb-12 space-y-6">
      {/* Navigation */}
      <Link
        to="/hr/my-jobs"
        className="inline-flex items-center text-gray-500 hover:text-red-600 font-medium transition-colors"
      >
        <ArrowLeft className="w-4 h-4 mr-2" />
        Back to Jobs
      </Link>

      {/* Header Card */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 md:p-8">
        <div className="flex flex-col md:flex-row justify-between gap-6">
          <div className="flex gap-5">
            <div className="w-16 h-16 bg-red-100 rounded-2xl flex items-center justify-center shrink-0 border border-red-200 text-red-700">
              <Briefcase className="w-8 h-8" />
            </div>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
                {job.jobTitle}
              </h1>
              <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600">
                <span className="flex items-center gap-1.5 px-3 py-1 bg-gray-100 rounded-full font-medium">
                  <Briefcase className="w-3.5 h-3.5" />
                  {job.jobPosition}
                </span>
                <span className="flex items-center gap-1.5">
                  <MapPin className="w-4 h-4 text-gray-400" />
                  {job.location || "Remote"}
                </span>
                <span className="flex items-center gap-1.5">
                  <Calendar className="w-4 h-4 text-gray-400" />
                  Posted {new Date(job.createdAt?.seconds * 1000).toLocaleDateString()}
                </span>
              </div>
            </div>
          </div>

          <div className="flex items-start">
            {/* Placeholder for future actions like Edit/Delete */}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content - Job Info */}
        <div className="lg:col-span-2 space-y-6">
          {/* Job Overview Grid */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-bold text-gray-900 mb-6">Job Overview</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <DetailItem
                icon={DollarSign}
                label="Salary Range"
                value={job.salary ? `$${Number(job.salary).toLocaleString()}` : "Not disclosed"}
              />
              <DetailItem
                icon={Clock}
                label="Experience"
                value={`${job.experience} Years`}
              />
              <DetailItem
                icon={GraduationCap}
                label="Education"
                value={job.education}
              />
              <DetailItem
                icon={Briefcase}
                label="Job Type"
                value={job.type || "Full Time"}
              />
            </div>
          </div>

          {/* Job Description */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 md:p-8">
            <h2 className="text-lg font-bold text-gray-900 mb-4">Job Description</h2>
            <div className="prose prose-red max-w-none text-gray-600 whitespace-pre-wrap leading-relaxed">
              {job.jobDescription}
            </div>
          </div>
        </div>

        {/* Sidebar - Company Info */}
        <div className="space-y-6">
          {company ? (
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-bold text-gray-900 mb-6 flex items-center gap-2">
                <Building className="w-5 h-5 text-red-600" />
                Company Details
              </h2>

              <div className="text-center mb-6">
                {company.avatar || company.logo ? (
                  <img
                    src={company.logo || company.avatar}
                    alt={company.company}
                    className="w-20 h-20 rounded-2xl mx-auto object-cover border border-gray-100 shadow-sm mb-3"
                  />
                ) : (
                  <div className="w-20 h-20 rounded-2xl mx-auto bg-gradient-to-br from-red-500 to-red-700 flex items-center justify-center text-white text-2xl font-bold mb-3 shadow-md">
                    {company.company?.charAt(0) || "C"}
                  </div>
                )}
                <h3 className="text-xl font-bold text-gray-900">{company.company || "Company Name"}</h3>
                <p className="text-sm text-gray-500">{company.location}</p>
              </div>

              <div className="space-y-4 pt-4 border-t border-gray-100">
                {company.website && (
                  <a
                    href={company.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 text-sm text-gray-600 hover:text-red-600 transition-colors bg-gray-50 p-3 rounded-xl"
                  >
                    <Globe className="w-4 h-4" />
                    <span className="font-medium truncate">{company.website.replace(/^https?:\/\//, '')}</span>
                  </a>
                )}

                {company.bio && (
                  <div className="text-sm text-gray-600 leading-relaxed bg-gray-50 p-4 rounded-xl">
                    <p className="font-medium text-gray-900 mb-1">About</p>
                    {company.bio}
                  </div>
                )}

                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-gray-50 p-3 rounded-xl text-center">
                    <p className="text-xs text-gray-500 uppercase tracking-wide font-medium">Headquarters</p>
                    <p className="text-sm font-semibold text-gray-900 mt-1">{company.location || "-"}</p>
                  </div>
                  <div className="bg-gray-50 p-3 rounded-xl text-center">
                    <p className="text-xs text-gray-500 uppercase tracking-wide font-medium">Founded</p>
                    <p className="text-sm font-semibold text-gray-900 mt-1">{company.foundedYear || "-"}</p>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-gray-50 rounded-2xl border border-gray-200 p-6 text-center text-gray-500">
              Company details not available
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default JobDetails;
